import React, { useState } from "react";
import InstantSearch from "@herb/components/Search/InstantSearch";
import { Index, connectInfiniteHits, Configure } from "react-instantsearch-dom";
import Image from "@herb/components/Image";
import { Link } from "gatsby";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import usePrefetchedData from "../../../hooks/usePrefetchedData";
import "./index.css";
import { fallbackImages } from "../../../utils/constants";

const CustomHits = connectInfiniteHits(({ hits, brandsToShow = 3 }) => {
  const threeRandomNumbers = [];
  for (let i = 0; i < brandsToShow; i++) {
    threeRandomNumbers.push(Math.floor(Math.random() * hits.length) + 1);
  }

  return (
    <React.Fragment>
      <BrandsDisplay hits={hits} brandsToShow={brandsToShow} randomNumbers={threeRandomNumbers} />
    </React.Fragment>
  );
});

// Shared brands display component
const BrandsDisplay = ({ hits, brandsToShow = 3, randomNumbers = null }) => {
  // Generate random numbers if not provided
  const numbers = randomNumbers || Array.from({ length: brandsToShow }, () => Math.floor(Math.random() * hits.length) + 1);
  
  return (
    <div className="articlePageRandomBrandsMainContainer">
      {brandsToShow === 2
        ? numbers.map(item => {
            const brand = hits[item - 1];
            return (
              <div className="articlePageSidebarRandomBrand" key={`brand-${item}`}>
                <div className="articlePageSidebarRandomBrandCardContent">
                  <Image
                    className="lazyload articlePageSidebarBrandImageArticlePage"
                    src={brand?.contentBrands?.logo?.sourceUrl || brand?.brand_image}
                    alt={brand?.title || brand?.name}
                    width={200}
                    fallback={fallbackImages.square}
                  />

                  <p className="articlePageSidebarArticlePageBrandTitle">
                    {brand?.title || brand?.name}
                  </p>
                </div>

                <div className="articlePageSidebarArticlePageBrandButton">
                  <Link
                    to={`/brands/${brand?.slug}`}
                    css={{
                      fontStyle: "italic",
                      fontWeight: "300",
                      padding: "6px 18px",
                      fontSize: "20px",
                      lineHeight: "24px",
                      display: "inline-block",
                      background: "#3041FF",
                      color: "white"
                    }}
                  >
                    VIEW BRAND
                  </Link>
                </div>
              </div>
            );
          })
        : numbers.map(item => {
            const brand = hits[item - 1];
            return (
              <div className="articlePageRandomBrand" key={`brand-${item}`}>
                <div className="articlePageRandomBrandCardContent">
                  <Image
                    className="lazyload brandImageArticlePage"
                    src={brand?.contentBrands?.logo?.sourceUrl || brand?.brand_image}
                    alt={brand?.title || brand?.name}
                    fallback={fallbackImages.square}
                  />

                  <h1 className="articlePageBrandTitle">
                    {brand?.title || brand?.name}
                  </h1>
                </div>

                <div className="articlePageBrandButton">
                  <Link
                    to={`/brands/${brand?.slug}`}
                    css={{
                      fontStyle: "italic",
                      fontWeight: "300",
                      padding: "6px 18px",
                      fontSize: "20px",
                      lineHeight: "24px",
                      display: "inline-block",
                      background: "#3041FF",
                      color: "white"
                    }}
                  >
                    VIEW BRAND
                  </Link>
                </div>
              </div>
            );
          })}
    </div>
  );
};

const LoadRandomBrands = ({
  brandsToShow,
  showHeading = true,
  headingFontSize = "30px",
  borderTop = "1px solid black;",
  lazyLoad = true
}) => {
  const [showAlgolia, setShowAlgolia] = useState(false);
  const { data: prefetchedBrands, isLoading: prefetchLoading } = usePrefetchedData("brands");
  
  // Lazy-load this component - only fetch when scrolled into view
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  const shouldShowPrefetched = !prefetchLoading && prefetchedBrands?.length > 0 && !showAlgolia;

  const renderPrefetchedContent = () => (
    <div
      css={{
        marginBottom: "20px",
        width: "100%"
      }}
      id="algolia"
    >
      {showHeading && (
        <p
          css={{ fontSize: headingFontSize }}
          className="articlePageFeaturedBrandHeading"
        >
          <span>Featured</span> Brands:
        </p>
      )}
      <BrandsDisplay hits={prefetchedBrands} brandsToShow={brandsToShow} />
    </div>
  );

  const renderContent = () => (
    <div
      css={{
        marginBottom: "20px",
        width: "100%"
      }}
      id="algolia"
    >
      {showHeading && (
        <p
          css={{ fontSize: headingFontSize }}
          className="articlePageFeaturedBrandHeading"
        >
          <span>Featured</span> Brands:
        </p>
      )}
      
      {/* Optimized query: reduced from 200 to 20 hitsPerPage */}
      <InstantSearch>
        <Index indexName="Brands">
          <Configure
            aroundLatLngViaIP={false}
            hitsPerPage={20}
            query="*"
            filters={"isBrandActive.brandActive:true"}
          />
          <CustomHits brandsToShow={brandsToShow} />
        </Index>
      </InstantSearch>
    </div>
  );

  // Wrap with lazy-loading if enabled
  if (lazyLoad) {
    return (
      <div ref={ref}>
        {isVisible ? (
          shouldShowPrefetched ? renderPrefetchedContent() : renderContent()
        ) : (
          <div css={{ minHeight: "200px" }} />
        )}
      </div>
    );
  }

  return shouldShowPrefetched ? renderPrefetchedContent() : renderContent();
};

export default LoadRandomBrands;
